import { useTranslation } from '@ufc/core';
import { cn, Button, Alert, AlertDescription } from '@ufc/uikit';
import type { ScalePanelState, WeighDirection, WeighStage } from '../../domain/types';
import type { WeighbridgeQueueItem } from '../../domain/types';

interface WeighScalePanelProps {
  panel: ScalePanelState;
  /** Active order number (from queue or manual entry) */
  orderNumber?: string;
  direction?: WeighDirection;
  /** Active queue item — provides recorded DB weights (firstWeightKg / secondWeightKg) */
  activeQueueItem?: WeighbridgeQueueItem | null;
  /** Whether this role may use the capture button (hardware trigger) */
  canCapture: boolean;
  onCapture?: () => void;
  /** Called when user confirms captured weight to save to Strapi */
  onSave?: () => void;
  saveDisabled?: boolean;
  /** Called when user clears a captured weight */
  onClearCapturedWeight?: () => void;
  /** Called when operator confirms overweight — allows Strapi submission to proceed */
  onOverweightContinue?: () => void;
  /** Called when operator rejects overweight — skips Strapi, goes directly to exit gate */
  onOverweightReject?: () => void;
  /** Which weighing stage this panel represents — enables live net weight display for second_weight */
  stage?: WeighStage;
  /** True while a save request is in-flight or the auto-flow is submitting.
   *  Disables Save & Clear buttons to prevent duplicate Strapi submissions. */
  isSaving?: boolean;
  className?: string;
}

function WeightValue({ weight }: { weight: number | null | undefined }) {
  const { t } = useTranslation();

  return (
    <span className="text-3xl font-bold tabular-nums tracking-tight">
      {formatWeight(weight)}{' '}
      <span className="text-base font-normal text-muted-foreground">
        {t('weighbridgeOps.panel.weightUnit')}
      </span>
    </span>
  );
}

function formatWeight(weight: number | null | undefined): string {
  if (typeof weight !== 'number' || Number.isNaN(weight)) {
    return '0';
  }
  return weight.toLocaleString();
}

function formatDelta(deltaKg: number): string {
  return deltaKg.toLocaleString();
}

export function WeighScalePanel({
  panel,
  orderNumber,
  direction = 'outbound',
  activeQueueItem,
  canCapture,
  stage,
  onCapture,
  onSave,
  saveDisabled = false,
  onClearCapturedWeight,
  onOverweightContinue,
  onOverweightReject,
  isSaving = false,
  className,
}: WeighScalePanelProps) {
  const { t } = useTranslation();

  const isCapturing = panel.status === 'capturing';
  const isError = panel.status === 'error';
  const isCaptured = panel.status === 'captured';

  const isSecondWeight = stage === 'second_weight';
  const isInbound = direction === 'inbound';
  const liveGross = isInbound
    ? activeQueueItem?.firstWeightKg ?? null
    : isCaptured && typeof panel.weight === 'number'
    ? panel.weight
    : null;
  const liveTare = isInbound
    ? isCaptured && typeof panel.weight === 'number'
      ? panel.weight
      : null
    : activeQueueItem?.firstWeightKg ?? null;
  const liveNet =
    liveGross !== null && liveTare !== null ? liveGross - liveTare : null;
  const firstRecordedLabel = isInbound
    ? 'weighbridgeOps.panel.gross'
    : 'weighbridgeOps.panel.tare';
  const secondRecordedLabel = isInbound
    ? 'weighbridgeOps.panel.tare'
    : 'weighbridgeOps.panel.gross';
  const liveFirstLabel = isInbound
    ? 'weighbridgeOps.panel.liveWeightSummary.inboundGross'
    : 'weighbridgeOps.panel.liveWeightSummary.tare';
  const liveSecondLabel = isInbound
    ? 'weighbridgeOps.panel.liveWeightSummary.inboundTare'
    : 'weighbridgeOps.panel.liveWeightSummary.gross';
  const missingFirstWeightLabel = isInbound
    ? 'weighbridgeOps.panel.liveWeightSummary.noGross'
    : 'weighbridgeOps.panel.liveWeightSummary.noTare';
  const firstSummaryWeight = isInbound ? liveGross : liveTare;
  const secondSummaryWeight = isInbound ? liveTare : liveGross;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Scale zero-check banner — shown while controller waits for scale to clear */}
      {panel.scaleZeroCheck && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-400/60 bg-amber-50/80 px-4 py-3 shadow-sm dark:border-amber-500/30 dark:bg-amber-950/20">
          <div className="flex shrink-0 items-center gap-1">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>
          <div className="flex flex-1 flex-col gap-0.5 min-w-0">
            <span className="text-xs font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">
              {t('weighbridgeOps.panel.zeroCheck.title')}
            </span>
            <span className="text-xs text-amber-600/80 dark:text-amber-400/70">
              {t('weighbridgeOps.panel.zeroCheck.reading', {
                weight: formatWeight(panel.scaleZeroCheck.weight),
              })}
            </span>
          </div>
          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-mono font-bold text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
            {panel.scaleZeroCheck.attempt}/{panel.scaleZeroCheck.maxAttempts}
          </span>
        </div>
      )}

      {/* Scale status display */}
      <div
        className={cn(
          'flex min-h-[140px] flex-col items-center justify-center rounded-xl border-2 p-6 text-center transition-colors duration-300 ease-in-out',
          isCapturing &&
            'border-yellow-400 bg-yellow-50/80 dark:bg-yellow-900/20 shadow-[0_0_20px_rgba(250,204,21,0.2)]',
          isCaptured &&
            'border-green-400 bg-green-50/80 dark:bg-green-900/20 shadow-[0_0_20px_rgba(74,222,128,0.2)]',
          isError &&
            'border-red-400 bg-red-50/80 dark:bg-red-900/20 shadow-[0_0_20px_rgba(248,113,113,0.2)]',
          panel.status === 'idle' &&
            'border-dashed border-muted-foreground/30 bg-muted/10'
        )}
      >
        {panel.status === 'idle' && (
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted/50 text-2xl text-muted-foreground/50 ring-1 ring-black/5 dark:ring-white/5">
              ⚖
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              {t('weighbridgeOps.panel.idle')}
            </p>
          </div>
        )}

        {isCapturing && (
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-1.5 rounded-full bg-yellow-100/50 px-3 py-1.5 dark:bg-yellow-900/30">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-2 w-2 rounded-full bg-yellow-500 animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
            <span className="text-sm font-bold tracking-wide text-yellow-700 dark:text-yellow-400 uppercase">
              {t('weighbridgeOps.panel.capturing')}
            </span>
            {orderNumber && (
              <span className="rounded-md bg-white/50 px-2 py-0.5 text-xs font-mono font-medium text-muted-foreground dark:bg-black/20">
                {orderNumber}
              </span>
            )}
          </div>
        )}

        {isCaptured && panel.weight !== undefined && (
          <div className="flex flex-col items-center gap-2">
            <span className="flex items-center gap-1 text-xs font-bold text-green-600 dark:text-green-400 uppercase tracking-widest">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
                ✓
              </span>
              {t('weighbridgeOps.panel.captured')}
            </span>
            <WeightValue weight={panel.weight} />
            <div className="flex items-center gap-2 mt-1">
              {orderNumber && (
                <span className="rounded-md bg-white/50 px-2 py-0.5 text-xs font-mono font-medium text-muted-foreground shadow-sm ring-1 ring-black/5 dark:bg-black/20 dark:ring-white/5">
                  {orderNumber}
                </span>
              )}
              {panel.capturedAt && (
                <span className="text-xs font-medium text-muted-foreground">
                  {new Date(panel.capturedAt).toLocaleTimeString('en-GB', {
                    hour12: false,
                  })}
                </span>
              )}
            </div>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-1">
            <span className="text-base text-red-500">✕</span>
            <span className="text-xs font-medium text-red-600 dark:text-red-400">
              {t('weighbridgeOps.panel.error')}
            </span>
            {panel.errorMessage && (
              <span className="text-xs text-muted-foreground line-clamp-2 max-w-[200px] text-center">
                {t(panel.errorMessage, { ...panel.errorParams, defaultValue: panel.errorMessage })}
              </span>
            )}
          </div>
        )}
      </div>

      {panel.overweightConfirm ? (
        <div className="rounded-xl border-2 border-red-500/60 bg-red-50/90 px-4 py-4 shadow-md dark:border-red-500/40 dark:bg-red-950/20">
          <p className="mb-1 text-sm font-bold uppercase tracking-widest text-red-700 dark:text-red-400">
            {t('weighbridgeOps.panel.overweightConfirm.title')}
          </p>
          <p className="mb-3 text-xs text-red-600 dark:text-red-300">
            {t('weighbridgeOps.panel.overweightConfirm.description', {
              netWeight: formatWeight(panel.overweightConfirm.netWeight),
              plannedWeight: formatWeight(panel.overweightConfirm.plannedWeight),
              deltaKg: formatDelta(panel.overweightConfirm.deltaKg),
            })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              className="flex-1"
              onClick={onOverweightReject}
            >
              {t('weighbridgeOps.panel.overweightConfirm.rejectBtn')}
            </Button>
            <Button
              variant="default"
              size="sm"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white dark:bg-green-700 dark:hover:bg-green-600"
              onClick={onOverweightContinue}
            >
              {t('weighbridgeOps.panel.overweightConfirm.continueBtn')}
            </Button>
          </div>
        </div>
      ) : panel.weightWarning ? (
        <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
          <AlertDescription className="text-xs">
            <span className="font-semibold">
              {t('weighbridgeOps.panel.overweightWarning.title')}
            </span>{' '}
            {t('weighbridgeOps.panel.overweightWarning.description', {
              plannedWeight: formatWeight(panel.weightWarning.plannedWeight),
              netWeight: formatWeight(panel.weightWarning.netWeight),
              deltaKg: formatDelta(panel.weightWarning.deltaKg),
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Capture button — hardware trigger only (Operator + Admin) */}
      {/* Recorded weights from DB (populated via order_details WS event) */}
      {activeQueueItem &&
        (activeQueueItem.firstWeightKg != null ||
          activeQueueItem.secondWeightKg != null) && (
          <div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2.5 text-xs">
            <p className="mb-1.5 font-bold uppercase tracking-widest text-muted-foreground">
              {t('weighbridgeOps.panel.recordedWeights')}
            </p>
            <div className="flex flex-wrap gap-3">
              {activeQueueItem.firstWeightKg != null && (
                <span className="text-foreground">
                  <span className="text-muted-foreground">
                    {t(firstRecordedLabel)}:{' '}
                  </span>
                  <span className="font-semibold">
                    {formatWeight(activeQueueItem.firstWeightKg)}
                  </span>
                </span>
              )}
              {activeQueueItem.secondWeightKg != null && (
                <span className="text-foreground">
                  <span className="text-muted-foreground">
                    {t(secondRecordedLabel)}:{' '}
                  </span>
                  <span className="font-semibold">
                    {formatWeight(activeQueueItem.secondWeightKg)}
                  </span>
                </span>
              )}
              {activeQueueItem.netWeightKg != null && (
                <span className="text-foreground">
                  <span className="text-muted-foreground">
                    {t('weighbridgeOps.panel.net')}:{' '}
                  </span>
                  <span className="font-semibold font-mono">
                    {formatWeight(activeQueueItem.netWeightKg)}
                  </span>
                </span>
              )}
            </div>
          </div>
        )}

      {canCapture && !isCaptured && (
        <Button
          onClick={onCapture}
          disabled={isCapturing || !orderNumber}
          variant={isError ? 'destructive' : 'default'}
          size="lg"
          className={cn(
            'h-12 w-full font-bold shadow-sm transition-all',
            !isError &&
              !isCapturing &&
              'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-md active:scale-[0.98]'
          )}
        >
          {isCapturing
            ? t('weighbridgeOps.panel.capturing')
            : isError
            ? t('weighbridgeOps.panel.override')
            : t('weighbridgeOps.panel.captureButton')}
        </Button>
      )}

      {/* Live weight summary — second weight stage only, shown once gross is captured */}
      {isSecondWeight && isCaptured && liveGross !== null && (
        <div className="rounded-xl border-2 border-emerald-400/60 bg-emerald-50/80 px-4 py-3.5 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-950/20">
          <p className="mb-2.5 text-xs font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
            {t('weighbridgeOps.panel.liveWeightSummary.title')}
          </p>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t(liveFirstLabel)}
              </span>
              {firstSummaryWeight !== null ? (
                <span className="font-semibold tabular-nums">
                  {formatWeight(firstSummaryWeight)}{' '}
                  <span className="text-xs font-normal text-muted-foreground">
                    {t('weighbridgeOps.panel.weightUnit')}
                  </span>
                </span>
              ) : (
                <span className="text-xs italic text-amber-600 dark:text-amber-400">
                  {t(missingFirstWeightLabel)}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t(liveSecondLabel)}
              </span>
              <span className="font-semibold tabular-nums">
                {formatWeight(secondSummaryWeight)}{' '}
                <span className="text-xs font-normal text-muted-foreground">
                  {t('weighbridgeOps.panel.weightUnit')}
                </span>
              </span>
            </div>
            {liveNet !== null && (
              <>
                <div className="my-1 border-t border-emerald-300/60 dark:border-emerald-600/30" />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
                    {t('weighbridgeOps.panel.liveWeightSummary.net')}
                  </span>
                  <span className="text-lg font-extrabold tabular-nums tracking-tight text-emerald-700 dark:text-emerald-400">
                    {formatWeight(liveNet)}{' '}
                    <span className="text-sm font-normal">
                      {t('weighbridgeOps.panel.weightUnit')}
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Save & Continue — shown after weight is captured via hardware OR admin override */}
      {isCaptured && panel.weight !== undefined && (
        <div className="flex gap-3">
          <Button
            onClick={onClearCapturedWeight}
            variant="outline"
            size="lg"
            disabled={isSaving}
            className="h-12 shrink-0 px-4 text-sm font-bold text-destructive border-destructive/30 bg-destructive/5 hover:bg-destructive/10 hover:text-destructive shadow-sm active:scale-[0.98] disabled:opacity-40"
          >
            {t('weighbridgeOps.panel.clearWeight')}
          </Button>
          <Button
            onClick={onSave}
            disabled={!orderNumber || isSaving || saveDisabled}
            size="lg"
            className="h-12 min-w-0 flex-1 font-bold bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md active:scale-[0.98] disabled:opacity-40"
          >
            {isSaving ? (
              <>
                <span className="mr-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                {t('weighbridgeOps.panel.saving')}
              </>
            ) : (
              <>
                <span className="mr-2 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px]">
                  ✓
                </span>
                {t('weighbridgeOps.panel.saveContinue')}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
